/**
 * mcc-content-tracking.js — in-article product-link click tracking (URL-derived).
 *
 * Fires on a click of ANY product link (`a[href*="/products/"]`) inside an
 * article page, deriving:
 *   - article_slug  from the page URL  (/blogs/{blog}/{slug})
 *   - product_handle from the link href (/products/{handle})
 * No `data-mcc-*` stamping required, so it covers EVERY in-article product-link
 * format (wss-prod, wss-deal, mega-pkmn__cta-product, mcc-injected-link,
 * sg-prod, …) automatically — present and future. Site chrome (header, footer,
 * nav, cart, search, product recommendations) is excluded so only editorial
 * content clicks are attributed.
 *
 * On a qualifying click it:
 *   1. writes first-party cart attributes via a SINGLE keepalive POST to
 *      /cart/update.js (ALWAYS — survives the click's navigation, no cart read);
 *   2. fires a GA4 `content_product_click` event (ONLY with analytics consent).
 *
 * Cart attributes are attribution EVIDENCE, not trusted data. First content
 * touch is remembered in localStorage (set once); last touch overwrites.
 *
 * 2026-10-12: links into the singles-hub deck builder (/collections/singles?build=
 * or ?deck=, e.g. a deck guide's "Build this deck") are tracked too, with
 * product_id "deck-builder:<slug>", so a guide -> builder -> order journey is
 * credited to the guide. Shopify's consent API is not on the page by default,
 * so it is loaded on demand before the GA4 consent check (it was always false).
 */
var MCC_FIRST_KEY = '_mcc_first_touch'
// Site chrome to EXCLUDE — a product link inside any of these is not an
// editorial content click.
var CHROME_SELECTOR = [
  'header', 'footer', 'nav', '[role="banner"]', '[role="contentinfo"]',
  '.header', '.site-header', '.footer', '.site-footer', '.announcement',
  '.cart', '.cart-drawer', '.mini-cart', '.drawer',
  '.search', '.predictive-search', '.search-modal',
  '.recommendations', '.product-recommendations', '.complementary-products',
  '.related-products', '.also-like', '.upsell',
].join(', ')

export function articleSlugFromPath(pathname) {
  var m = /^\/blogs\/[^/]+\/([^/?#]+)/.exec(pathname || '')
  return m ? m[1] : null
}

export function productHandleFromHref(href) {
  var m = /\/products\/([^/?#]+)/.exec(href || '')
  if (m) return m[1]
  // a link into the singles-hub deck builder counts as a content click on that deck
  var b = /\/collections\/singles\?(?:[^#]*&)?(?:build|deck)=([^&#]+)/.exec(href || '')
  return b ? 'deck-builder:' + decodeURIComponent(b[1]).slice(0, 100) : null
}

export function readFirstTouch(storage) {
  try { return JSON.parse((storage || window.localStorage).getItem(MCC_FIRST_KEY) || 'null') }
  catch (e) { return null }
}

/** Build the cart-attribute payload from derived slug + handle. `firstTouch` is
 * the stored first content touch (or null → THIS click is the first). No cart
 * read required, so it survives navigation. */
export function buildCartAttributes(slug, handle, clickId, nowIso, firstTouch) {
  var last = { click_id: clickId, article_slug: slug, product_id: handle, clicked_at: nowIso }
  var first = firstTouch || last
  var attrs = { _mcc_attribution_model: 'last_content_touch' }
  for (var k in last) { if (last[k] != null) attrs['_mcc_last_' + k] = last[k] }
  for (var k2 in first) { if (first[k2] != null) attrs['_mcc_first_' + k2] = first[k2] }
  return attrs
}

export function onProductLinkClick(opts) {
  // opts: { slug, handle, clickId, now, consent, gtag, cartWrite }
  if (opts.consent && typeof opts.gtag === 'function') {
    opts.gtag('event', 'content_product_click', {
      click_id: opts.clickId, article_slug: opts.slug, product_id: opts.handle,
    })
  }
  opts.cartWrite(opts.slug, opts.handle, opts.clickId, opts.now)   // always (first-party)
}

// --- DOM binding (exercised in the browser) ---
if (typeof document !== 'undefined') {
  var uuid = function () {
    try { if (crypto && crypto.randomUUID) return crypto.randomUUID() } catch (e) {}
    return 'clk-' + Date.now() + '-' + Math.floor(Math.random() * 1e9)
  }
  var analyticsConsent = function () {
    try {
      return !!(window.Shopify && window.Shopify.customerPrivacy &&
        window.Shopify.customerPrivacy.analyticsProcessingAllowed())
    } catch (e) { return false }
  }
  // The consent API is not loaded on the page by default: load it once, early,
  // so the click-time check above can answer (it fails closed until loaded).
  try {
    if (window.Shopify && !window.Shopify.customerPrivacy && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {})
    }
  } catch (e) {}
  var cartWrite = function (slug, handle, clickId, nowIso) {
    var first = readFirstTouch(window.localStorage)
    if (!first) {
      first = { click_id: clickId, article_slug: slug, product_id: handle, clicked_at: nowIso }
      try { window.localStorage.setItem(MCC_FIRST_KEY, JSON.stringify(first)) } catch (e) {}
    }
    var attrs = buildCartAttributes(slug, handle, clickId, nowIso, first)
    try {
      fetch('/cart/update.js', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      }).catch(function () {})
    } catch (e) {}
  }
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href*="/products/"], a[href*="/collections/singles?"]')
    if (!link) return
    var slug = articleSlugFromPath(location.pathname)
    if (!slug) return                                 // not an article page
    if (link.closest(CHROME_SELECTOR)) return         // site chrome, not content
    var handle = productHandleFromHref(link.getAttribute('href'))
    if (!handle) return
    onProductLinkClick({
      slug: slug, handle: handle, clickId: uuid(), now: new Date().toISOString(),
      consent: analyticsConsent(), gtag: window.gtag, cartWrite: cartWrite,
    })
  }, true)
}
